
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getTranslationKeys, getTranslationValue } from './translationUtils';
import i18n from '@/lib/i18n';

interface TranslationsTableProps {
  selectedLocale: string;
  availableLocales: string[];
  onLocaleChange: (locale: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

export function TranslationsTable({
  selectedLocale,
  availableLocales,
  onLocaleChange,
  isLoading,
  setIsLoading
}: TranslationsTableProps) {
  const { t, i18n: i18nInstance } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [translationKeys, setTranslationKeys] = useState<string[]>([]);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [saveInProgress, setSaveInProgress] = useState<string | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    // Only get the translation keys if we're not already loading
    if (!isLoading) {
      // Get all translation keys
      const keys = getTranslationKeys();
      setTranslationKeys(keys);
      
      // Initialize editing values for the selected locale
      const initialValues: Record<string, string> = {};
      keys.forEach(key => {
        initialValues[key] = getTranslationValue(selectedLocale, key);
      });
      setEditingValues(initialValues);
    }
  }, [selectedLocale, isLoading]);
  
  const handleSaveTranslation = async (key: string) => {
    // Prevent multiple saves for the same key
    if (saveInProgress === key || isLoading) {
      return;
    }
    
    setSaveInProgress(key);
    setIsLoading(true);
    
    try {
      // Save translation to database
      if (!i18n.saveTranslation) {
        throw new Error('saveTranslation method not available');
      }
      
      const success = await i18n.saveTranslation(selectedLocale, key, editingValues[key]);
      
      if (!success) {
        throw new Error('Failed to save translation to database');
      }
      
      toast({
        title: t('success'),
        description: t('translationUpdated', { key, locale: selectedLocale }) || 
          `Translation for "${key}" in "${selectedLocale}" updated successfully`,
      });
    } catch (error) {
      console.error("Error saving translation:", error);
      toast({
        title: 'Error saving translation',
        description: 'An error occurred while saving the translation',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setSaveInProgress(null);
    }
  };
  
  // Filter translation keys based on search query
  const filteredKeys = translationKeys.filter(key => 
    key.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <Select
          value={selectedLocale}
          onValueChange={onLocaleChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('selectLanguage')} />
          </SelectTrigger>
          <SelectContent>
            {availableLocales.map(locale => (
              <SelectItem key={locale} value={locale}>
                {locale === 'en' ? 'English' : 
                 locale === 'es' ? 'Español' : 
                 locale === 'fr' ? 'Français' : 
                 locale}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('searchTranslationKeys')}
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('translationKey')}</TableHead>
              <TableHead>{t('translationValue')}</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    {t('loading')}
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredKeys.length > 0 ? (
              filteredKeys.map(key => (
                <TableRow key={key}>
                  <TableCell className="font-mono text-xs">{key}</TableCell>
                  <TableCell>
                    <Input
                      value={editingValues[key] || ''}
                      onChange={(e) => setEditingValues({
                        ...editingValues,
                        [key]: e.target.value
                      })}
                      placeholder={getTranslationValue('en', key)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="sm" 
                      onClick={() => handleSaveTranslation(key)}
                      disabled={isLoading || saveInProgress === key}
                    >
                      {saveInProgress === key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : t('save')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  {t('noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
